import React from "react";
import SettingsLayout from "@/components/SettingsLayout";
import OrgScopeEmptyState from "@/components/OrgScopeEmptyState";
import { useOrgScope } from "@/hooks/useOrgScope";
import { useAuth } from "@/contexts/AuthContext";

/**
 * v1.2 PR-C.1 — Route-level org-scope guard.
 *
 * The previous SettingsLayout guard replaced its own `{children}` with the
 * empty-state when `org_id == null`, but the wrapped page component itself
 * was still mounted by React Router — so its top-level `useEffect(() =>
 * load(), [])` fired, hit a 400 from `require_org_scope`, and surfaced a
 * "No org context" toast that the user reported as redundant noise.
 *
 * This wrapper lifts the decision one level up: when `noOrgSuperAdmin` is
 * true, the page component is **never instantiated**. Instead, we render
 * the empty-state inside SettingsLayout chrome (so the settings rail stays
 * visible and the user can still pick a different tab or open the inline
 * org picker).
 *
 * After the super_admin picks an org via the picker, the AuthContext
 * refresh propagates `effective_org_id`, `noOrgSuperAdmin` flips to false,
 * this wrapper re-renders, and the original page component mounts cleanly
 * in the impersonated org's context.
 *
 * v1.2 PR-C.2 — `key={effectiveOrgId}` forces React to fully unmount and
 * remount the wrapped page when the active org context flips (start of
 * impersonation, end of impersonation, or switching between impersonated
 * orgs). Prevents the previously-observed bug where the impersonated org's
 * stale data lingered on screen after `End impersonation` until the user
 * manually navigated to another route.
 */
export default function OrgScopedRoute({ children }) {
  const { noOrgSuperAdmin } = useOrgScope();
  const { effectiveOrgId } = useAuth();
  if (noOrgSuperAdmin) {
    return (
      <SettingsLayout>
        <OrgScopeEmptyState />
      </SettingsLayout>
    );
  }
  // React.Fragment supports `key`; this re-mounts the subtree on context flip.
  return <React.Fragment key={effectiveOrgId || "no-org"}>{children}</React.Fragment>;
}
